import {
  getGetChannelsApiV1QueryKey,
  useDeleteChannelApiV1,
  useGetChannelsApiV1Suspense,
} from "@/api/__generated__/api.schema.ts";
import { Profile } from "@/components/profile";
import { Allowed } from "@/components/role/allowed";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useChannelRegisterFlow } from "@/routes/channels/components/register/use-channel-register-flow";
import {
  Badge,
  Button,
  Icon,
  TableBody,
  TableCell,
  TableHead,
  TableHeadRow,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
  cn,
  useConfirm,
} from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

export function ChannelTable({ className }: { className?: string }) {
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items },
    },
  } = useGetChannelsApiV1Suspense(workspaceId);
  const channels = items.filter((item) => item.platform === "YOUTUBE_SHORTS");

  return (
    <div className={cn("border border-borderLight rounded-[12px] pt-2 bg-backgroundLight overflow-scroll", className)}>
      <table className="w-full">
        <TableHeader className="bg-backgroundLight">
          <TableHeadRow>
            <TableHead className="pl-4 desktop:pl-6">Channel Name</TableHead>
            <TableHead>Subscribers</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Added date</TableHead>
            <TableHead />
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {channels.length === 0 ? (
            <ChannelTable.Empty />
          ) : (
            channels.map((channel) => (
              <TableRow key={channel.id} className="bg-white">
                <TableCell className="pl-4 desktop:pl-6 py-3 max-w-[360px]">
                  <Profile
                    name={channel.name}
                    description={channel.customUrl}
                    profileImageUrl={channel.profileImageUrl}
                  />
                </TableCell>
                <TableCell className="text-textMuted">{channel.subscriberCount.toLocaleString()}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {
                    {
                      UNVERIFIED: <ChannelVerifyButton channelId={channel.id} />,
                      APPROVED: <Badge theme="green">Approved</Badge>,
                      PENDING: (
                        <ChannelTableStatusInfo
                          title="Verifying Your Channel"
                          contents="We're verifying your channel for compliance with internal policies. This review may take up to 24 hours. If
                            you don't hear back, please contact our support team."
                        >
                          <Badge theme="orange">Under review</Badge>
                        </ChannelTableStatusInfo>
                      ),
                      REJECTED: (
                        <ChannelTableStatusInfo
                          title="Channel Not Approved"
                          contents="Your channel registration has been denied because we found content that does not comply with internal
                            policies. If you would like to appeal this decision or have questions, please contact the support team."
                        >
                          <Badge theme="red">Rejected</Badge>
                        </ChannelTableStatusInfo>
                      ),
                    }[channel.reviewStatus]
                  }
                </TableCell>
                <TableCell className="text-textMuteds">
                  {Temporal.Instant.from(channel.registeredAt)
                    .toZonedDateTimeISO(Temporal.Now.timeZoneId())
                    .toLocaleString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                </TableCell>
                <TableCell>
                  <Allowed.Admin>
                    <ChannelDeleteButton channelName={channel.name} channelId={channel.id} />
                  </Allowed.Admin>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </table>
    </div>
  );
}

function ChannelDeleteButton({ channelName, channelId }: { channelName: string; channelId: string }) {
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const { mutate: deleteChannel, isPending } = useDeleteChannelApiV1({
    mutation: {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [...getGetChannelsApiV1QueryKey(workspaceId)],
        });
      },
    },
  });

  return (
    <button
      type="button"
      className="flex items-center justify-center"
      onClick={async () => {
        const confirmed = await confirm({
          title: `Delete '${channelName}'`,
          description:
            "Are you sure you want to delete this channel? Deleting it will stop earnings from being counted.",
          confirmText: "Delete channel",
          confirmVariant: "error",
          cancelText: "Cancel",
        });

        if (confirmed) {
          deleteChannel({ workspaceId, channelId });
        }
      }}
      disabled={isPending}
    >
      <Icon name="trash" />
    </button>
  );
}

function ChannelVerifyButton({ channelId }: { channelId: string }) {
  const { open } = useChannelRegisterFlow();
  return (
    <Button
      variant="outlined"
      onClick={() =>
        open({
          defaultStep: 3,
          channelUrl: `https://www.youtube.com/channel/${channelId}`,
          channelId,
        })
      }
    >
      Verify
    </Button>
  );
}

ChannelTable.Empty = function ChannelTableEmpty() {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={5} className="text-center text-white bg-white">
        <div className="space-y-3 py-12">
          <Icon name="videos" bgColor="brandTangerine" iconColor="white" iconSize="24px" className="size-14" />
          <div className="space-y-2">
            <Typography size="md" color="textPrimary" weight={600}>
              No channels connected yet
            </Typography>
            <Typography size="xs" color="textMuted">
              Add your first channel to start managing and tracking your YouTube Shorts
            </Typography>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

function ChannelTableStatusInfo({
  children,
  contents,
}: { children: React.ReactNode; title: string; contents: string }) {
  return (
    <Tooltip>
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipContent position="topMiddle" side="bottom">
        {contents}
      </TooltipContent>
    </Tooltip>
  );
}
